import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { LegacyColumnDef as ColumnDef } from '@tanstack/react-table/legacy'

import { PagedDataTable } from '@/components/data-table/PagedDataTable'
import { Empty } from '@/components/feedback/Empty'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { useCubeLiveQuery } from '@/hooks/use-cube-live-query'
import { formatCubeDateTime, formatInteger } from '@/lib/format'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import {
  kantarDurumQuery,
  latestSync,
  syncDurumQuery,
  toKantarDurum,
  toSyncDurum,
  type SyncDurum,
} from '@/services/cube/system'

const noop = () => undefined

const sync = messages.system.fields

// Sunucu sıralaması yok (Cube sorgusu sabit sırada): tüm sütunlar `enableSorting: false`.
const SYNC_COLUMNS: ColumnDef<SyncDurum>[] = [
  {
    id: 'kantar_id',
    header: sync.kantar_id,
    accessorFn: (row) => row.kantarId,
    enableSorting: false,
  },
  { id: 'tablo', header: sync.tablo, accessorFn: (row) => row.tablo, enableSorting: false },
  {
    id: 'son_sync',
    header: sync.son_sync,
    accessorFn: (row) => formatCubeDateTime(row.sonSync),
    enableSorting: false,
  },
  {
    id: 'son_tam_yukleme',
    header: sync.son_tam_yukleme,
    accessorFn: (row) => formatCubeDateTime(row.sonTamYukleme),
    enableSorting: false,
  },
  {
    id: 'kac_saat_once',
    header: sync.kac_saat_once,
    accessorFn: (row) => row.kacSaatOnce,
    enableSorting: false,
    cell: ({ row }) => `${formatInteger(row.original.kacSaatOnce)} ${messages.system.hoursSuffix}`,
    meta: {
      numeric: true,
      exportValue: (row: SyncDurum) => row.kacSaatOnce ?? '',
      exportNumberFormat: '#,##0',
    },
  },
]

/** `rapor.v_kantar` + `rapor.v_sync_durum`, Cube aboneliğiyle canlı (bkz. cube/model/kantar/sistem.yml). */
export function SystemPage() {
  const kantarQuery = useCubeLiveQuery({
    queryKey: queryKeys.cube('KantarDurum'),
    query: kantarDurumQuery,
    select: toKantarDurum,
  })
  const syncQuery = useCubeLiveQuery({
    queryKey: queryKeys.cube('SyncDurum'),
    query: syncDurumQuery,
    select: toSyncDurum,
  })
  const kantarlar = kantarQuery.data ?? []
  const syncDurum = syncQuery.data ?? []
  const lastSync = latestSync(syncDurum)
  const isLoading = kantarQuery.isLoading || syncQuery.isLoading
  const isError = kantarQuery.isError || syncQuery.isError
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold">{messages.system.title}</h1>
          <p className="text-muted-foreground text-sm">{messages.system.description}</p>
        </div>
        <p className="text-muted-foreground text-xs">
          {messages.system.lastSync}: {formatCubeDateTime(lastSync)}
        </p>
      </div>

      {isLoading ? (
        <Loading rows={6} />
      ) : isError ? (
        <ErrorState
          error={kantarQuery.error ?? syncQuery.error}
          onRetry={() => {
            void kantarQuery.refetch()
            void syncQuery.refetch()
          }}
        />
      ) : kantarlar.length === 0 && syncDurum.length === 0 ? (
        <Empty />
      ) : (
        <>
          <section className="flex flex-col gap-2">
            <h2 className="text-muted-foreground text-sm font-medium">
              {messages.system.kantarSectionTitle}
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {kantarlar.map((kantar) => (
                <Card key={kantar.kantarId}>
                  <CardHeader className="flex-row items-center justify-between">
                    <CardTitle>{kantar.kantarAdi ?? kantar.kantarId}</CardTitle>
                    <Badge variant={kantar.baglantiDurumu === 'online' ? 'success' : 'outline'}>
                      {kantar.baglantiDurumu ?? messages.common.unknown}
                    </Badge>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-1 text-sm">
                    <span className="text-muted-foreground">
                      {messages.system.fields.son_gorulme}
                    </span>
                    <span className="text-right">{formatCubeDateTime(kantar.sonGorulme)}</span>
                    <span className="text-muted-foreground">
                      {messages.system.fields.ajan_surumu}
                    </span>
                    <span className="text-right">
                      {kantar.ajanSurumu ?? messages.common.unknown}
                    </span>
                    <span className="text-muted-foreground">
                      {messages.system.fields.kac_dakika_once}
                    </span>
                    <span className="tabular text-right">
                      {formatInteger(kantar.kacDakikaOnce)} {messages.system.minutesSuffix}
                    </span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section>
            <PagedDataTable<SyncDurum>
              tableId="sistem-senkron"
              title={messages.system.syncSectionTitle}
              columns={SYNC_COLUMNS}
              data={syncDurum}
              total={syncDurum.length}
              page={1}
              pageSize={Math.max(syncDurum.length, 1)}
              onPageChange={noop}
              exportFilename="senkron-durumu"
            />
          </section>
        </>
      )}
    </div>
  )
}
